import { type Ref, ref } from "vue";
import * as VueApolloComposable from "@vue/apollo-composable";
import {
  type ApolloQueryResult,
  NetworkStatus,
  type OperationVariables,
} from "@apollo/client/core";
import { cloneDeep, forIn, isObject } from "lodash";
import type { ReactiveFunction } from "@/graphql";

type Variables<V> = V | Ref<V> | ReactiveFunction<V>;

type Options<R, V extends OperationVariables> =
  | VueApolloComposable.UseQueryOptions<R, V>
  | Ref<VueApolloComposable.UseQueryOptions<R, V>>
  | ReactiveFunction<VueApolloComposable.UseQueryOptions<R, V>>;

export type QueryType<V extends OperationVariables, R> = (
  variables: Variables<V>,
  options: Options<R, V>,
) => VueApolloComposable.UseQueryReturn<R, V>;

export type RemoveRecursively<T, Drop = "__typename"> =
  T extends Array<infer E>
    ? Array<RemoveRecursively<E, Drop>>
    : T extends object
      ? {
          [K in Exclude<keyof T, Drop>]: RemoveRecursively<T[K], Drop>;
        }
      : T;

export type OptionalRecursively<T, Drop = "__typename"> =
  T extends Array<infer E>
    ? Array<OptionalRecursively<E, Drop>>
    : T extends object
      ? {
          [K in Extract<keyof T, Drop>]?: OptionalRecursively<T[K], Drop>;
        } & {
          [K in Exclude<keyof T, Drop>]: OptionalRecursively<T[K], Drop>;
        }
      : T;

function omitDeep(obj: Record<string, unknown>) {
  forIn(obj, (value, key) => {
    if (isObject(value)) {
      omitDeep(value as any);
    } else if (key === "__typename") {
      delete obj[key];
    }
  });
}

export function deepCopyWithTypenameStripping<T extends Record<string, any>>(
  object: T,
): RemoveRecursively<T> {
  const data = cloneDeep(object);
  omitDeep(data);
  return data as unknown as RemoveRecursively<T>;
}

/**
 * this function appends a mutable result to the regular use query result
 * @param useQuery query to be executed
 * @param variables Variables of the useQuery
 * @param options  Options of the useQuery (if not overwritten fetch policy is cache-and-network)
 * @param shouldReplaceMutableResult callback function when network status is not ready or refetch
 * @returns useQuery Results with mutable result object
 */
function useMutateGraphQL<S, T extends QueryType<any, any>, U>(
  useQuery: T,
  variables: Parameters<T>["0"],
  defaultValue: S,
  pick: (queryResult: ReturnType<T>["result"]["value"]) => U,
  shouldReplaceMutableResult?: (queryResult: ApolloQueryResult<ReturnType<T>["result"]>) => boolean,
  options?: Parameters<T>["1"],
) {
  // @ts-expect-error the useQuery<any, any> type is not matching with the return type of the template query
  const queryReturn: ReturnType<T> = useQuery(variables, options ?? {});

  const mutableResult = ref<S | U>(defaultValue);

  if (queryReturn.result.value != null) {
    const returnWithoutTypename = deepCopyWithTypenameStripping(queryReturn.result);
    mutableResult.value = pick(returnWithoutTypename) as any;
  }

  queryReturn.onResult((result) => {
    if (
      [NetworkStatus.ready, NetworkStatus.refetch].includes(result.networkStatus) ||
      shouldReplaceMutableResult == null ||
      shouldReplaceMutableResult?.(result) === true
    ) {
      const returnWithoutTypename = deepCopyWithTypenameStripping(result?.data);
      mutableResult.value = pick(returnWithoutTypename) as any;
    }
  });

  const mutableQueryReturn = {
    ...queryReturn,
    mutableResult,
  };

  return mutableQueryReturn;
}

export default useMutateGraphQL;
